/**
 * Parent connectors (W2-02; DESIGN.md §4.8): dashed arrows from a parent's anchor to its child's, for the active
 * line (its parent and its children) and the selected lines, so a rig reads at a glance.
 */

import type { AppServices } from '../../../app/registry/types';
import type { LineId } from '../../../core/model/types';
import type { OverlayDrawContext, PreviewOverlayProvider } from '../../../render/types';
import { dp, frameInfo } from './shared';
import { overlayTheme } from './theme';

function arrow(
  c: OverlayDrawContext,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  color: string,
  strong: boolean,
): void {
  const ctx = c.ctx;
  const len = Math.hypot(bx - ax, by - ay);
  if (len < dp(c, 4)) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.globalAlpha = strong ? 0.95 : 0.6;
  ctx.lineWidth = dp(c, strong ? 1.5 : 1);
  ctx.setLineDash([dp(c, 5), dp(c, 4)]);
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  ctx.stroke();
  ctx.setLineDash([]);
  // Arrow head at the child end.
  const ux = (bx - ax) / len;
  const uy = (by - ay) / len;
  const h = dp(c, 8);
  ctx.beginPath();
  ctx.moveTo(bx, by);
  ctx.lineTo(bx - ux * h - uy * h * 0.5, by - uy * h + ux * h * 0.5);
  ctx.lineTo(bx - ux * h + uy * h * 0.5, by - uy * h - ux * h * 0.5);
  ctx.closePath();
  ctx.fill();
  // Parent end marker.
  ctx.beginPath();
  ctx.arc(ax, ay, dp(c, 3), 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export function createParentLinksOverlay(services: AppServices): PreviewOverlayProvider {
  return {
    id: 'preview.parentLinks',
    order: 40,
    draw(c) {
      const store = services.store;
      if (store === null) return;
      const st = services.editor.getState();
      const doc = store.doc;
      const { states } = frameInfo(c);
      const theme = overlayTheme();
      const pairs = new Map<string, { parent: LineId; child: LineId; strong: boolean }>();
      const add = (parent: LineId, child: LineId, strong: boolean): void => {
        const k = `${String(parent)}>${String(child)}`;
        const prev = pairs.get(k);
        if (prev === undefined || (strong && !prev.strong)) pairs.set(k, { parent, child, strong });
      };
      const focus = new Set<LineId>(st.selection.lines);
      if (st.activeLine !== null) focus.add(st.activeLine);
      for (const id of focus) {
        const line = doc.lines.get(id);
        if (line === undefined) continue;
        const strong = id === st.activeLine;
        if (line.parent !== null) add(line.parent, id, strong);
        for (const child of store.idx.childrenOf(id)) add(id, child, strong);
      }
      for (const { parent, child, strong } of pairs.values()) {
        const p = states.get(parent);
        const q = states.get(child);
        if (p === undefined || q === undefined) continue;
        const [ax, ay] = c.worldToPx(p.x, p.y);
        const [bx, by] = c.worldToPx(q.x, q.y);
        arrow(c, ax, ay, bx, by, strong ? theme.accent : theme.info, strong);
      }
    },
  };
}
